import initials from 'initials'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import trackEvents from '../../constants/track-events'
import { trackEvent } from '../../helpers/analytics'
import { useChartData } from '../../hooks/use-chart-data'

const MAX_VISIBLE = 8

const getInitials = (space) =>
  initials(space.initials || (space.identification || '').substring(0, 3))

const Spaces = ({ spaces }) => {
  const { t } = useTranslation()
  const { data } = useChartData({ spaces })
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const popoverWrapperRef = useRef(null)

  const allSpaces = data.spaces
  const visibleSpaces = allSpaces.slice(0, MAX_VISIBLE)
  const hiddenCount = allSpaces.length - MAX_VISIBLE

  const filtered = search
    ? allSpaces.filter((s) =>
        (s.identification || '').toLowerCase().includes(search.toLowerCase())
      )
    : allSpaces

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (
        popoverWrapperRef.current &&
        !popoverWrapperRef.current.contains(e.target)
      ) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const goToTalent = (space) => {
    trackEvent(trackEvents.VIEW_TALENT_TEAMANALYSIS)
    navigate(`/talent/${space.id}`)
  }

  return (
    <div className='flex flex-col items-start justify-between gap-6 lg:flex-row'>
      <div className='w-full max-w-lg'>
        <div>
          <h3 className='text-xl font-semibold leading-6 text-gray-900'>
            {t('team_analyse.spaces.title')}
          </h3>
          <p className='mt-2 max-w-4xl text-sm text-gray-500'>
            {t('team_analyse.spaces.description')}
          </p>
        </div>

        <div className='relative mt-4 flex items-center'>
          {visibleSpaces.map((space, index) => (
            <button
              key={space.id || index}
              title={space.identification}
              onClick={() => goToTalent(space)}
              style={{
                backgroundColor: space.borderColor,
                marginLeft: index === 0 ? 0 : -8,
                zIndex: index
              }}
              className='relative inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-white font-medium text-white transition-opacity hover:opacity-80'
            >
              <span className='text-sm leading-none'>{getInitials(space)}</span>
            </button>
          ))}

          {hiddenCount > 0 && (
            <div
              ref={popoverWrapperRef}
              className='relative'
              style={{ marginLeft: -8, zIndex: MAX_VISIBLE }}
            >
              <button
                onClick={() => setOpen((v) => !v)}
                className='inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-gray-100 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-200'
              >
                +{hiddenCount}
              </button>

              {open && (
                <div className='absolute left-0 top-12 z-50 w-64 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl'>
                  <div className='border-b border-gray-100 p-2'>
                    <input
                      autoFocus
                      type='text'
                      placeholder='Suchen...'
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className='w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100'
                    />
                  </div>
                  <ul className='max-h-60 overflow-y-auto py-1'>
                    {filtered.map((space, index) => (
                      <li key={space.id || index}>
                        <button
                          onClick={() => {
                            setOpen(false)
                            goToTalent(space)
                          }}
                          className='flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-50'
                        >
                          <span
                            style={{ backgroundColor: space.borderColor }}
                            className='inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white'
                          >
                            {getInitials(space)}
                          </span>
                          <span className='truncate text-sm text-gray-700'>
                            {space.identification}
                          </span>
                        </button>
                      </li>
                    ))}
                    {filtered.length === 0 && (
                      <li className='px-3 py-4 text-center text-sm text-gray-400'>
                        Keine Ergebnisse
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className='mt-6 rounded-lg bg-gray-50 p-6 shadow-sm ring-1 ring-gray-900/5 lg:mt-0'>
        <h1 className='text-xl font-semibold text-gray-900'>
          {t('team_analyse.spaces.interpretation_guide.title')}
        </h1>
        <p className='mt-1 max-w-lg text-sm text-gray-700'>
          {t('team_analyse.spaces.interpretation_guide.description')}
        </p>
        <div className='mt-6'>
          <a
            target='_blank'
            rel='noreferrer'
            onClick={() =>
              trackEvent(trackEvents.DOWNLOAD_INTERPRETATIONGUIDE_TEAMANALYSIS)
            }
            href={`https://aivy-assets.s3.eu-central-1.amazonaws.com/pdfs/teamanalysis_guide_de.pdf?random_number=${new Date().getTime()}`}
            className='text-sm font-semibold leading-6 text-gray-900'
          >
            {t('team_analyse.spaces.interpretation_guide.cta')}{' '}
            <span aria-hidden='true'>&rarr;</span>
          </a>
        </div>
      </div>
    </div>
  )
}

Spaces.propTypes = {
  spaces: PropTypes.array.isRequired
}

export default Spaces
